import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   getValue: () => string,
 *   updateQuantity: (stepMultiplier: number) => void,
 *   setQuantity: (event: { target: HTMLInputElement, preventDefault: () => void }) => void,
 *   updateButtonStates: () => void,
 *   refs: { quantityInput?: HTMLInputElement, minusButton?: HTMLButtonElement, plusButton?: HTMLButtonElement }
 * }} QuantitySelector
 */

/**
 * @typedef {Object} PurchaseRailRefs
 * @property {HTMLElement} stickyBar - The rail container (always present)
 * @property {HTMLButtonElement} [addToCartButton] - The rail's Add to Bag button (absent when sold out)
 * @property {HTMLInputElement} [railQuantityInput] - The rail's quantity input (absent when sold out)
 * @property {HTMLButtonElement} [railMinus] - The rail's decrease button
 * @property {HTMLButtonElement} [railPlus] - The rail's increase button
 * @property {HTMLButtonElement} [notifyButton] - The rail's back-in-stock trigger (sold-out state only)
 */

/**
 * Persistent Purchase Rail component.
 *
 * Reuses the native product form as the single source of truth: the Add to Bag button
 * puppets the native add-to-cart button, and the rail quantity controls drive the native
 * quantity-selector-component (no parallel cart or quantity state). Visibility is handled
 * purely by CSS (mobile = fixed/persistent, desktop = static), so this component contains
 * no scroll/visibility logic.
 *
 * @extends {Component<PurchaseRailRefs>}
 */
class StickyAddToCartComponent extends Component {
  // Only the bar is guaranteed to exist. The action/quantity refs are absent in the
  // sold-out / unavailable state, so they must not be required.
  requiredRefs = ['stickyBar'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** Diagnostic warning is emitted once per instance, not on every variant morph. @type {boolean} */
  #warnedNoTarget = false;

  /** Watches for Klaviyo injecting / removing its back-in-stock button. @type {MutationObserver | null} */
  #notifyObserver = null;

  /** @type {number | undefined} */
  #notifyFrame;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#cacheTargetButton();
    this.#watchNotifyTrigger();
    // Mirror the native quantity state into the rail once the native component is ready.
    requestAnimationFrame(() => this.#syncRailQuantity());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#notifyObserver?.disconnect();
    if (this.#notifyFrame) cancelAnimationFrame(this.#notifyFrame);
  }

  // --- Add to Bag (puppets the native add-to-cart button) ---

  /**
   * Handles the Add to Bag button click in the rail.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    // Brief added-state confirmation on the rail button.
    addToCartButton.dataset.added = 'true';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    await onAnimationEnd([addToCartButton]);
    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  // --- Back in stock (puppets Klaviyo's injected button) ---

  /**
   * Handles the rail's back-in-stock click by forwarding it to the button Klaviyo injects
   * into the native (hidden) buy-buttons form. Klaviyo owns the form and reads the selected
   * variant from the native product form at click time, so no part of the subscription is
   * reimplemented here — this only makes the trigger reachable.
   */
  handleNotifyClick = () => {
    const trigger = this.#getNotifyTrigger();

    // Vanished since the last check (app removed mid-session, markup changed): re-evaluate
    // and hide, rather than leaving a button that silently does nothing.
    if (!trigger) {
      this.#refreshNotifyState();
      return;
    }

    trigger.click();
  };

  /**
   * Klaviyo injects its button asynchronously, and re-injects it on variant change, so the
   * rail observes rather than assumes. Attribute writes are not observed (childList only),
   * so refreshing the state cannot retrigger this; work is coalesced to one check a frame.
   */
  #watchNotifyTrigger() {
    this.#refreshNotifyState();

    this.#notifyObserver = new MutationObserver(() => {
      if (this.#notifyFrame) return;
      this.#notifyFrame = requestAnimationFrame(() => {
        this.#notifyFrame = undefined;
        this.#refreshNotifyState();
      });
    });

    this.#notifyObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Reveals the rail's trigger only while Klaviyo's real button is in the DOM. Without it
   * the rail shows the plain sold-out status — the behaviour before back in stock existed.
   */
  #refreshNotifyState() {
    this.dataset.notifyReady = this.#getNotifyTrigger() ? 'true' : 'false';
  }

  /**
   * Gets Klaviyo's injected back-in-stock button for THIS product. Scoped to the product
   * form (Klaviyo injects it beside the native add-to-cart button) so the rail can never
   * drive another product's trigger, and so a missing form fails closed.
   * @returns {HTMLButtonElement | null}
   */
  #getNotifyTrigger() {
    const productForm = this.#getProductForm();
    return productForm
      ? /** @type {HTMLButtonElement | null} */ (productForm.querySelector('#klaviyo-bis-button-container button'))
      : null;
  }

  // --- Quantity bridge (rail controls -> native quantity-selector-component) ---

  /**
   * Decrease the native quantity by one step. The native component validates, clamps,
   * and broadcasts QuantitySelectorUpdateEvent, which mirrors back into the rail.
   */
  railDecreaseQuantity = () => {
    this.#getNativeQuantity()?.updateQuantity(-1);
  };

  /**
   * Increase the native quantity by one step.
   */
  railIncreaseQuantity = () => {
    this.#getNativeQuantity()?.updateQuantity(1);
  };

  /**
   * Apply a directly-typed quantity to the native component (reusing its validation),
   * which then mirrors the clamped value back into the rail.
   */
  railSetQuantity = () => {
    const native = this.#getNativeQuantity();
    const input = this.refs.railQuantityInput;
    if (!native || !(input instanceof HTMLInputElement)) return;

    const nativeInput = native.refs?.quantityInput;
    if (!(nativeInput instanceof HTMLInputElement)) return;

    nativeInput.value = input.value;
    native.setQuantity({ target: nativeInput, preventDefault() {} });
  };

  /**
   * Select the rail input value on focus for quick replacement.
   * @param {Event} event
   */
  selectRailInput = (event) => {
    if (event.target instanceof HTMLInputElement) event.target.select();
  };

  // --- Native event handlers ---

  /**
   * Handles variant update events by morphing in the freshly rendered rail.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newRail = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newRail) return;

    const newBar = newRail.querySelector('[ref="stickyBar"]');
    if (!newBar) return;

    // Morph the rail contents (purchasable <-> sold-out swap, updated quantity rules, etc.)
    morph(this.refs.stickyBar, newBar, { childrenOnly: true });

    this.dataset.variantAvailable = newRail.dataset.variantAvailable;
    const variant = event.detail.resource;
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the puppet target and re-mirror quantity after the DOM swap.
    // Native preserves the chosen quantity across a variant switch (the product form is
    // not re-rendered); mirror it now and again next frame so the rail reliably reflects
    // the preserved value rather than the freshly-rendered default.
    this.#cacheTargetButton();
    this.#syncRailQuantity();
    requestAnimationFrame(() => this.#syncRailQuantity());

    // The sold-out branch (and Klaviyo's button with it) comes and goes with the variant.
    // The observer catches this too, but refreshing here keeps the swap in the same frame
    // as the morph rather than a frame behind it.
    this.#refreshNotifyState();
  };

  /**
   * Tracks the selected variant id; the variant update event handles the morph.
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (variantId) this.dataset.currentVariantId = variantId;
  };

  /**
   * Handles cart add completion. Always resets the puppet flag; on a *successful* add for
   * this product, resets the native quantity to its minimum (the native add-to-cart flow
   * remains the source of truth — this only reacts to its event).
   * @param {CustomEvent} event
   */
  #handleCartAddComplete = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    const data = event?.detail?.data;
    const isSuccessfulAddForThisProduct =
      event?.type === ThemeEvents.cartUpdate &&
      data &&
      !data.didError &&
      data.source === 'product-form-component' &&
      String(data.productId) === this.dataset.productId;

    if (isSuccessfulAddForThisProduct) {
      this.#resetQuantity();
    }
  };

  /**
   * Mirrors native quantity changes into the rail. No-ops when the rail has no quantity
   * controls (sold-out / unavailable), so no binding occurs against missing elements.
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    // Ignore cart-drawer quantity selectors; only reflect the product page selector.
    if (event.detail.cartLine) return;
    this.#syncRailQuantity();
  };

  // --- Helpers ---

  /**
   * Resets the native quantity to its minimum via the native component's validated API.
   * The native component then broadcasts the change, which mirrors back into the rail.
   * No-ops when there is no native quantity component (e.g. sold-out).
   */
  #resetQuantity() {
    const native = this.#getNativeQuantity();
    const nativeInput = native?.refs?.quantityInput;
    if (!native || !(nativeInput instanceof HTMLInputElement)) return;

    nativeInput.value = nativeInput.min || '1';
    native.setQuantity({ target: nativeInput, preventDefault() {} });
  }

  /**
   * Reflects the native quantity value and button states into the rail.
   * Guards on the rail controls existing, so it is safe in the sold-out state.
   */
  #syncRailQuantity() {
    const native = this.#getNativeQuantity();
    const input = this.refs.railQuantityInput;
    if (!native || !(input instanceof HTMLInputElement)) return;

    // The native component dispatches its update event BEFORE recomputing its own button
    // states, so force a recompute from the now-current value before mirroring — otherwise
    // the rail's minus/plus disabled state lags one step behind the native quantity.
    native.updateButtonStates?.();

    input.value = native.getValue();

    const { railMinus, railPlus } = this.refs;
    if (railMinus instanceof HTMLButtonElement) railMinus.disabled = native.refs?.minusButton?.disabled ?? false;
    if (railPlus instanceof HTMLButtonElement) railPlus.disabled = native.refs?.plusButton?.disabled ?? false;
  }

  /**
   * Caches the native add-to-cart button this rail puppets.
   *
   * The rail is a PUPPET: with no native form to drive, Add to Bag and the quantity stepper
   * become silent no-ops — the pill still renders and still looks enabled. That exact
   * misconfiguration shipped once (the `buy-buttons` block was removed from the default product
   * template while tidying hidden blocks), and it produced no error anywhere. So say so.
   */
  #cacheTargetButton() {
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm
      ? /** @type {HTMLButtonElement | null} */ (productForm.querySelector('[ref="addToCartButton"]'))
      : null;

    if (!this.#targetAddToCartButton && !this.#warnedNoTarget) {
      this.#warnedNoTarget = true;
      console.warn(
        '[purchase-rail] No native add-to-cart button found for product %s in this section. ' +
          'The "Buy buttons" block is missing from this product template — the rail puppets it, ' +
          'so Add to Bag and the quantity stepper will do nothing. Add Buy buttons back to the ' +
          'product template (it renders no visible UI; the rail hides it).',
        this.dataset.productId
      );
    }
  }

  /**
   * Gets the native quantity-selector-component (single source of truth for quantity).
   * @returns {QuantitySelector | null}
   */
  #getNativeQuantity() {
    const productForm = this.#getProductForm();
    return productForm
      ? /** @type {QuantitySelector | null} */ (productForm.querySelector('quantity-selector-component'))
      : null;
  }

  /**
   * Gets the native product form for this product within the current section.
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    return sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
